import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import type {
  Empresa,
  EmpresaCreatePayload,
  EmpresaUpdatePayload,
  EmpresasFilter,
  EmpresasResult,
} from '../types/empresa';
import { cuitDigits } from '../utils/format';

/**
 * Hooks de acceso a la tabla `empresas` via cliente Supabase.
 *
 * El aislamiento por rol esta a cargo de las policies RLS de Postgres,
 * no del frontend. Estos hooks asumen que el usuario tiene sesion activa
 * y delegan en la BD el filtrado por permisos.
 */

const QK = {
  all: ['empresas'] as const,
  list: (filter: EmpresasFilter) => ['empresas', 'list', filter] as const,
  detail: (id: string) => ['empresas', 'detail', id] as const,
};

const DEFAULT_PAGE_SIZE = 25;

// ----- LIST -----

async function fetchEmpresas(filter: EmpresasFilter): Promise<EmpresasResult> {
  const page = filter.page ?? 0;
  const pageSize = filter.pageSize ?? DEFAULT_PAGE_SIZE;
  const from = page * pageSize;
  const to = from + pageSize - 1;

  let query = supabase
    .from('empresas')
    .select('*', { count: 'exact' })
    .order('razon_social', { ascending: true })
    .range(from, to);

  if (filter.estado && filter.estado !== 'todas') {
    query = query.eq('estado', filter.estado);
  }

  if (filter.search && filter.search.trim()) {
    const term = filter.search.trim();
    // Buscar por razon social (case-insensitive) o por CUIT.
    // Los CUITs en BD se guardan sin guiones; normalizamos el termino antes.
    const cuitClean = cuitDigits(term);
    if (cuitClean.length >= 2) {
      // El usuario probablemente esta buscando por CUIT (o un termino mixto).
      // Hacemos OR: razon_social LIKE %term% O cuit LIKE %cuitClean%.
      // Importante: si el termino es PURO numerico, el match por razon_social
      // no va a aportar nada (ningun nombre tiene digitos), pero no rompe.
      query = query.or(
        `razon_social.ilike.%${term}%,cuit.ilike.%${cuitClean}%`
      );
    } else {
      query = query.ilike('razon_social', `%${term}%`);
    }
  }

  const { data, error, count } = await query;
  if (error) throw error;

  return {
    data: (data ?? []) as Empresa[],
    count: count ?? 0,
    page,
    pageSize,
  };
}

export function useEmpresas(filter: EmpresasFilter = {}) {
  return useQuery({
    queryKey: QK.list(filter),
    queryFn: () => fetchEmpresas(filter),
    placeholderData: (prev) => prev, // mantener data vieja durante refetch (evita flicker en paginacion)
  });
}

// ----- DETAIL -----

async function fetchEmpresa(id: string): Promise<Empresa | null> {
  const { data, error } = await supabase
    .from('empresas')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data as Empresa | null;
}

export function useEmpresa(id: string | undefined) {
  return useQuery({
    queryKey: id ? QK.detail(id) : ['empresas', 'detail', 'none'],
    queryFn: () => fetchEmpresa(id!),
    enabled: !!id,
  });
}

// ----- CREATE -----

async function createEmpresa(payload: EmpresaCreatePayload): Promise<Empresa> {
  // Normalizar CUIT (sin guiones para guardar uniforme; el formateo es de UI)
  const normalized = { ...payload, cuit: cuitDigits(payload.cuit) };
  const { data, error } = await supabase
    .from('empresas')
    .insert(normalized)
    .select('*')
    .single();
  if (error) throw error;
  return data as Empresa;
}

export function useCreateEmpresa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createEmpresa,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.all });
    },
  });
}

// ----- UPDATE -----

async function updateEmpresa({
  id,
  payload,
}: {
  id: string;
  payload: EmpresaUpdatePayload;
}): Promise<Empresa> {
  const normalized = { ...payload };
  if (normalized.cuit) normalized.cuit = cuitDigits(normalized.cuit);

  const { data, error } = await supabase
    .from('empresas')
    .update(normalized)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as Empresa;
}

export function useUpdateEmpresa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateEmpresa,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: QK.all });
      qc.setQueryData(QK.detail(data.id), data);
    },
  });
}

// ----- DELETE (soft via estado) -----

async function setEmpresaEstado({
  id,
  estado,
}: {
  id: string;
  estado: 'activa' | 'inactiva' | 'suspendida';
}): Promise<Empresa> {
  const { data, error } = await supabase
    .from('empresas')
    .update({ estado })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as Empresa;
}

export function useSetEmpresaEstado() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: setEmpresaEstado,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: QK.all });
      qc.setQueryData(QK.detail(data.id), data);
    },
  });
}
